import { type ReactNode, useRef } from 'react';

import { useInView } from '@/hooks/use-in-view';
import { useReducedMotion } from '@/hooks/use-reduced-motion';
import { cssEase, DURATION } from '@/lib/motion-tokens';
import { cn } from '@/lib/utils';

export interface ImageRevealProps {
  children: ReactNode;
  className?: string;
  /** Wipe direction the reveal opens *from*. */
  from?: 'left' | 'right' | 'bottom' | 'top';
  duration?: number;
  /** Retained for API compatibility; the reveal fires via the useInView hook. */
  start?: string;
}

const CLIP_HIDDEN: Record<NonNullable<ImageRevealProps['from']>, string> = {
  left: 'inset(0 100% 0 0)',
  right: 'inset(0 0 0 100%)',
  bottom: 'inset(100% 0 0 0)',
  top: 'inset(0 0 100% 0)',
};

/**
 * Scroll-triggered curtain-wipe reveal for a single media block: the content is
 * clipped, then unclipped as it enters view while the inner media eases from a
 * slight zoom.
 *
 * Reveal fires via the rect-based {@link useInView} hook (not
 * IntersectionObserver, which never fires inside a `preserve-3d` ancestor such
 * as a TiltCard) so media is never left stuck hidden. Shows instantly under
 * reduced motion.
 */
export function ImageReveal({
  children,
  className,
  from = 'left',
  duration = DURATION.cinematic,
}: ImageRevealProps) {
  const ref = useRef<HTMLDivElement>(null);
  const reduced = useReducedMotion();
  const inView = useInView(ref, { once: true, threshold: 0.9, disabled: reduced });

  const revealed = reduced || inView;
  const ease = cssEase();

  return (
    <div
      ref={ref}
      className={cn('overflow-hidden', className)}
      style={{
        clipPath: revealed ? 'inset(0 0 0 0)' : CLIP_HIDDEN[from],
        transition: reduced ? undefined : `clip-path ${duration}s ${ease}`,
      }}
    >
      <div
        className="h-full"
        style={{
          transform: revealed ? 'scale(1)' : 'scale(1.18)',
          transition: reduced ? undefined : `transform ${duration}s ${ease}`,
          willChange: 'transform',
        }}
      >
        {children}
      </div>
    </div>
  );
}
